import React, { useMemo } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useGetShareAction } from './getAction';

type ShareActionComponentProps = {
	children: React.ReactElement<{ onClick?: () => void }>;
	onBeforeAction?: () => void | Promise<void>;
	onAfterAction?: (success: boolean) => void | Promise<void>;
};

export type ShareAction<CH extends ShareChannel = ShareChannel> = {
	channel: CH;
	url: string;
	shortUrl?: string;
	/**
	 * @deprecated use `ShareAction` component property instead - this method will be removed in a future release
	 */
	action: () => Promise<boolean>;
	// FIXME can we make this type require a specific onClick prop?
	// This currently does not prevent passing components that do not accept onClick
	ShareAction: (props: ShareActionComponentProps) => React.JSX.Element;
};

type Options<CH extends ShareChannel = ShareChannel> = {
	config: ShareConfig;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getShortUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useShareActions<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{ getUrl, ...options }: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const getShareAction = useGetShareAction(options);
	const { getShortUrl } = options;
	return useMemo(
		() =>
			channels.map((channel) => {
				const url = getUrl(channel);
				const shortUrl = getShortUrl(channel);
				const shareActionFn = getShareAction(channel);

				return {
					channel,
					url,
					shortUrl: url === shortUrl ? undefined : shortUrl,
					action: shareActionFn,
					ShareAction: ({ children, onBeforeAction, onAfterAction }: ShareActionComponentProps) => {
						const onClick = useMemo(() => {
							return () => {
								void (async () => {
									await onBeforeAction?.();
									const success = await shareActionFn();
									await onAfterAction?.(success);
								})();
							};
						}, [onBeforeAction, onAfterAction]);

						return React.cloneElement(children, {
							...children.props,
							onClick,
						});
					},
				};
			}),
		[channels, getShareAction, getUrl, getShortUrl],
	);
}
